import React, { useEffect, useRef, useState } from 'react';
import LiveChoiceButtons from './LiveChoiceButtons';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { SellerBankAccount } from '../../../../Component/Interface/Store';
import BankAccounts from '../../Setting/BankAccounts';

export type PaymentPolicy = {
  payment_mode: 'pg' | 'combined' | 'bank';
  bank_account_seq?: number | null;
  bank_name: string;
  bank_num: string;
  depositor: string;
  deposit_deadline: string | null;
  pg_deadline?: string | null;
};

export const defaultPaymentDeadline = (finish?: string | Date | null, days = 2) => {
  if (!finish) return null;
  const value = typeof finish === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(finish)
    ? finish.replace(' ', 'T') + '+09:00' : finish;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? new Date(time + days * 24 * 60 * 60 * 1000).toISOString() : null;
};
export const readPaymentPolicy = (data: Partial<PaymentPolicy> = {}, liveFinish?: string | Date | null): PaymentPolicy => ({
  payment_mode: data.payment_mode === 'combined' || data.payment_mode === 'bank' ? data.payment_mode : 'pg',
  bank_account_seq: data.bank_account_seq ?? null,
  bank_name: data.bank_name || '', bank_num: data.bank_num || '', depositor: data.depositor || '',
  deposit_deadline: data.deposit_deadline || defaultPaymentDeadline(liveFinish),
  pg_deadline: data.pg_deadline || defaultPaymentDeadline(liveFinish),
});

export const paymentPolicyError = (policy: PaymentPolicy) => {
  if (policy.pg_deadline && !Number.isFinite(Date.parse(policy.pg_deadline))) return 'PG 결제 기한을 확인해주세요.';
  return policy.payment_mode !== 'pg'
    && (!policy.bank_name.trim() || !policy.bank_num.trim() || !policy.depositor.trim() || !policy.deposit_deadline)
    ? `${policy.payment_mode === 'bank' ? '무통장' : '복합'} 결제의 등록 입금 계좌와 입금 기한을 확인해주세요.` : '';
};

export const deadlineInputValue = (value: string | null) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '';
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(value));
  const part = (type: string) => parts.find(item => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
};

export default function PaymentPolicyFields({ value, onChange, disabled = false, modeDisabled = false, liveFinish, buttonChoices = false, manageAccounts = false }: {
  value: PaymentPolicy; onChange: (value: PaymentPolicy) => void; disabled?: boolean; modeDisabled?: boolean; liveFinish?: string | Date | null; buttonChoices?: boolean; manageAccounts?: boolean;
}) {
  const [accountManagerOpen, setAccountManagerOpen] = useState(false);
  const [accounts, setAccounts] = useState<SellerBankAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [error, setError] = useState('');
  const defaultDeadline = defaultPaymentDeadline(liveFinish);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (value.payment_mode === 'pg' || disabled || accountsLoaded) return;
    let cancelled = false;
    setAccountsLoading(true);
    setError('');
    api.get(`${SERVER_ADDRESS}store/bank-accounts`, { params: { purpose: 'live' } })
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data?.accounts)) throw new Error('Invalid accounts');
        const active = (data.accounts as SellerBankAccount[])
          .filter(account => account.active).sort((a, b) => b.seq - a.seq);
        setAccounts(active);
        setAccountsLoaded(true);
        const current = valueRef.current;
        if (active.length && !current.bank_account_seq && !current.bank_name && !current.bank_num && !current.depositor) {
          const latest = active[0];
          onChangeRef.current({ ...current, bank_account_seq: latest.seq, bank_name: latest.bank_name,
            bank_num: latest.bank_num, depositor: latest.depositor });
        }
      })
      .catch(() => { if (!cancelled) setError('등록 계좌를 불러오지 못했습니다. 다시 열어주세요.'); })
      .finally(() => { if (!cancelled) setAccountsLoading(false); });
    return () => { cancelled = true; };
  }, [value.payment_mode, disabled, accountsLoaded]);

  const change = (fields: Partial<PaymentPolicy>) => { setError(''); onChange({ ...value, ...fields }); };
  const deadline = deadlineInputValue(value.deposit_deadline);
  const changeDeadline = (date: string, hour: string) => change({ deposit_deadline: date && hour
    ? new Date(`${date}T${hour}:00:00+09:00`).toISOString() : null });
  const pgDeadline = deadlineInputValue(value.pg_deadline || null);
  const changePgDeadline = (date: string, hour: string) => change({ pg_deadline: date && hour
    ? new Date(`${date}T${hour}:00:00+09:00`).toISOString() : null });
  const changePaymentDeadline = (days: number) => {
    const deadline = defaultPaymentDeadline(liveFinish, days);
    change({ ...(value.payment_mode !== 'pg' ? { deposit_deadline: deadline } : {}),
      ...(value.payment_mode !== 'bank' ? { pg_deadline: deadline } : {}) });
  };
  const isPaymentDeadlineSelected = (days: number) => {
    const preset = defaultPaymentDeadline(liveFinish, days);
    if (!preset) return false;
    const activeDeadlines = value.payment_mode === 'pg' ? [value.pg_deadline]
      : value.payment_mode === 'bank' ? [value.deposit_deadline] : [value.pg_deadline, value.deposit_deadline];
    return activeDeadlines.every(deadline => !!deadline && Date.parse(deadline) === Date.parse(preset));
  };
  const hasSavedAccount = !!(value.bank_name && value.bank_num && value.depositor);
  const selectedAccountExists = accounts.some(account => account.seq === value.bank_account_seq);
  const savedOutsideList = hasSavedAccount && !selectedAccountExists;
  const selectedAccountValue = selectedAccountExists ? value.bank_account_seq : savedOutsideList ? 'saved' : '';
  const changeAccount = (selected: string) => {
    if (selected === 'manage') { setAccountManagerOpen(true); return; }
    const account = accounts.find(item => item.seq === Number(selected));
    if (account) change({ bank_account_seq: account.seq, bank_name: account.bank_name, bank_num: account.bank_num, depositor: account.depositor });
    else if (selected !== 'saved') change({ bank_account_seq: null, bank_name: '', bank_num: '', depositor: '' });
  };
  const hours = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));

  return <><Fields disabled={disabled}>
    <legend>고객 결제 수단</legend>
    <Options>
      <label className={value.payment_mode === 'pg' ? 'selected' : ''}><input type="radio" name="live-payment-mode"
        checked={value.payment_mode === 'pg'} disabled={modeDisabled}
        onChange={() => change({ payment_mode: 'pg' })} />PG 결제만</label>
      <label className={value.payment_mode === 'combined' ? 'selected' : ''}><input type="radio" name="live-payment-mode"
        checked={value.payment_mode === 'combined'} disabled={modeDisabled}
        onChange={() => change({ payment_mode: 'combined' })} />복합 결제 (PG 결제 및 무통장 입금)</label>
      <label className={value.payment_mode === 'bank' ? 'selected' : ''}><input type="radio" name="live-payment-mode"
        checked={value.payment_mode === 'bank'} disabled={modeDisabled}
        onChange={() => change({ payment_mode: 'bank' })} />무통장 입금만</label>
    </Options>
    {value.payment_mode !== 'pg' && <>
      <AccountSection>
        {buttonChoices ? <LiveChoiceButtons label="등록 입금 계좌" value={String(selectedAccountValue || '')} disabled={accountsLoading} searchable
          onChange={changeAccount} options={[
            { value: '', label: accountsLoading ? '등록 계좌를 불러오는 중…' : '계좌를 선택해 주세요' },
            ...(savedOutsideList ? [{ value: 'saved', label: `이 라이브에 저장된 계좌 · ${value.bank_name} ${value.bank_num}` }] : []),
            ...accounts.map(account => ({ value: String(account.seq), label: `${account.label} · ${account.bank_name} ${account.bank_num}` })),
            ...(manageAccounts ? [{ value: 'manage', label: '통장 편집…' }] : []),
          ]} /> : <label><span>등록 입금 계좌 <span className="required" aria-hidden="true">*</span></span>
          <select aria-label="등록 입금 계좌" aria-required="true" value={selectedAccountValue || ''} disabled={accountsLoading}
            onChange={event => changeAccount(event.target.value)}>
            <option value="">{accountsLoading ? '등록 계좌를 불러오는 중…' : '계좌를 선택해 주세요'}</option>
            {savedOutsideList && <option value="saved">이 라이브에 저장된 계좌 · {value.bank_name} {value.bank_num}</option>}
            {accounts.map(account => <option key={account.seq} value={account.seq}>
              {account.label} · {account.bank_name} {account.bank_num}
            </option>)}
            {manageAccounts && <optgroup label="통장 관리"><option value="manage">통장 편집…</option></optgroup>}
          </select>
        </label>}
        {error && <p role="alert">{error}</p>}
        {accountsLoaded && !accounts.length && !hasSavedAccount && <p>등록된 입금 계좌가 없습니다. 셀러 설정에서 계좌를 등록해 주세요.</p>}
      </AccountSection>
      <DeadlineSection>
        <strong>무통장 입금 기한 (한국시간) <span className="required" aria-hidden="true">*</span></strong>
        <DeadlineControls>
          <label>날짜<input aria-label="날짜" aria-required="true" type="date" value={deadline.slice(0, 10)}
            onChange={event => changeDeadline(event.target.value, deadline.slice(11, 13) || '00')} /></label>
          {buttonChoices ? <LiveChoiceButtons label="시각" value={deadline.slice(11, 13)} disabled={disabled}
            onChange={hour => changeDeadline(deadline.slice(0, 10), hour)} options={[{ value: '', label: '선택' }, ...hours.map(hour => ({ value: hour, label: `${hour}:00` }))]} />
            : <label>시각<select aria-label="시각" aria-required="true" value={deadline.slice(11, 13)}
            onChange={event => changeDeadline(deadline.slice(0, 10), event.target.value)}>
            <option value="">선택</option>
            {Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0')).map(hour =>
              <option key={hour} value={hour}>{hour}:00</option>)}</select></label>}
        </DeadlineControls>
        <p>방송 종료 2일(48시간) 후가 기본값입니다.</p>
      </DeadlineSection>
    </>}
    {value.payment_mode !== 'bank' && <DeadlineSection>
      <strong>PG 결제 기한 (한국시간)</strong>
      <DeadlineControls>
        <label>PG 날짜<input aria-label="PG 결제 기한 날짜" type="date" value={pgDeadline.slice(0, 10)}
          onChange={event => changePgDeadline(event.target.value, pgDeadline.slice(11, 13) || '00')} /></label>
        {buttonChoices ? <LiveChoiceButtons label="PG 결제 기한 시각" value={pgDeadline.slice(11, 13)} disabled={disabled}
          onChange={hour => changePgDeadline(pgDeadline.slice(0, 10), hour)} options={[{ value: '', label: '선택' }, ...hours.map(hour => ({ value: hour, label: `${hour}:00` }))]} />
          : <label>PG 시각<select aria-label="PG 결제 기한 시각" value={pgDeadline.slice(11, 13)}
          onChange={event => changePgDeadline(pgDeadline.slice(0, 10), event.target.value)}>
          <option value="">선택</option>
          {Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0')).map(hour =>
            <option key={hour} value={hour}>{hour}:00</option>)}</select></label>}
      </DeadlineControls>
      <p>방송 종료 2일(48시간) 후가 기본값입니다.</p>
    </DeadlineSection>}
    <DeadlinePresets role="group" aria-label="결제기한 빠른 선택">
      {[2, 3, 5].map(days => <ClearDeadline key={days} type="button" disabled={!defaultDeadline}
        aria-pressed={isPaymentDeadlineSelected(days)}
        onClick={() => changePaymentDeadline(days)}>결제기한 {days}일</ClearDeadline>)}
    </DeadlinePresets>
  </Fields>
    {manageAccounts && accountManagerOpen && <BankAccounts disabled={disabled} selection={{
      selectedSeq: value.bank_account_seq,
      onClose: () => setAccountManagerOpen(false),
      onAccountsChange: updated => {
        setAccounts(updated.filter(account => account.active).sort((a, b) => b.seq - a.seq));
        const current = valueRef.current;
        if (updated.some(account => account.seq === current.bank_account_seq && !account.active)) {
          onChangeRef.current({ ...current, bank_account_seq: null, bank_name: '', bank_num: '', depositor: '' });
        }
      },
      onSelect: account => {
        change({ bank_account_seq: account.seq, bank_name: account.bank_name, bank_num: account.bank_num, depositor: account.depositor });
        setAccountManagerOpen(false);
      },
    }} />}
  </>;
}

const Fields = styled.fieldset`
  box-sizing: border-box; width: 100%; min-width: 0; margin: 24px 0 0;
  padding: 22px 0 0; border: 0; border-top: 1px solid var(--admin-theme-border, #e8e4ed);
  &:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
  legend { padding: 0; color: var(--admin-theme-text, #302a38); font-size: 18px; font-weight: 600; }
  label { display: flex; flex-direction: column; gap: 8px; min-width: 0; color: var(--admin-theme-text, #494450); font-size: 14px; }
  input:not([type='radio']), select { box-sizing: border-box; width: 100%; min-width: 0; min-height: 44px; padding: 10px 12px; border: 1px solid var(--admin-theme-border, #cfc9d9); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #302a38); font: inherit; font-size: 16px; }
  button:focus-visible, input:focus-visible, select:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  p { margin: 10px 0; color: var(--admin-theme-muted, #625d6a); font-size: 13px; line-height: 1.5; }
  [role='alert'] { color: var(--admin-theme-danger, #b42318); }
  .required { color: var(--admin-theme-danger, #d84059); font-weight: 800; }
`;
const Options = styled.div`
  display: flex; flex-wrap: wrap; gap: 24px; margin: 18px 0 24px;
  label { flex-direction: row; align-items: center; min-height: 28px; cursor: pointer; line-height: 1.4; }
  input { flex: 0 0 22px; width: 22px; height: 22px; margin: 0; accent-color: var(--admin-theme-link, #6338dc); }
  @media (max-width: 650px) { gap: 14px; flex-direction: column; align-items: flex-start; }
`;
const AccountSection = styled.div`min-width: 0;`;
const DeadlineSection = styled.div`
  margin-top: 22px;
  > strong { display: block; margin-bottom: 10px; color: var(--admin-theme-text, #494450); font-size: 14px; }
`;
const DeadlineControls = styled.div`
  display: grid; grid-template-columns: minmax(0, 2fr) minmax(120px, 1fr); gap: 12px;
  > fieldset { grid-column: 1 / -1; }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;
const DeadlinePresets = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); margin-top: var(--space-3);
`;
const ClearDeadline = styled.button`
  min-height: var(--control-lg); padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; cursor: pointer;
  &[aria-pressed='true'] { background: var(--color-brand-subtle); border-color: var(--color-brand); color: var(--color-text-link); font-weight: 600; }
  &:hover:not(:disabled) { border-color: var(--color-brand); color: var(--color-text-link); }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: not-allowed; }
`;
